import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50">
      <nav className="backdrop-blur-xl bg-white/80 border-b border-slate-200/50 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </Button>
          <h1 className="text-lg sm:text-xl font-bold text-slate-900">Privacy Policy</h1>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10">
          <div className="prose prose-slate max-w-none">
            <p className="text-sm text-slate-600 mb-6">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduzione</h2>
            <p className="mb-4">
              <strong>Facile Digitale Srls</strong> ("noi", "nostro" o "la Società"), titolare del trattamento, 
              si impegna a proteggere la privacy degli utenti di traveldiscount.ai ("Servizio"). 
              Questa Privacy Policy descrive come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali 
              in conformità con il Regolamento Generale sulla Protezione dei Dati (GDPR) UE 2016/679.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">2. Dati Raccolti</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">2.1 Dati forniti dall'utente</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account:</strong> nome completo, email, password (criptata)</li>
              <li><strong>Viaggiatori:</strong> preferenze di viaggio, richieste di soggiorno, date, numero ospiti</li>
              <li><strong>Proprietari:</strong> nome struttura, indirizzo, descrizioni proprietà, prezzi, disponibilità</li>
              <li><strong>Comunicazioni:</strong> messaggi inviati tramite la piattaforma</li>
              <li><strong>Pagamenti:</strong> dati transazionali (gestiti da provider terzi sicuri)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-6">2.2 Dati raccolti automaticamente</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Log di utilizzo:</strong> indirizzo IP, tipo di browser, pagine visitate, orari di accesso</li>
              <li><strong>Cookies:</strong> per migliorare l'esperienza utente e analizzare l'uso del Servizio</li>
              <li><strong>Dati dispositivo:</strong> tipo di dispositivo, sistema operativo</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">3. Finalità del Trattamento</h2>
            <p className="mb-4">Utilizziamo i tuoi dati per:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Fornire e gestire il Servizio (base contrattuale)</li>
              <li>Elaborare prenotazioni e pagamenti</li>
              <li>Facilitare la comunicazione tra viaggiatori e proprietari</li>
              <li>Inviare notifiche su prenotazioni, offerte e aggiornamenti del servizio</li>
              <li>Migliorare il Servizio attraverso analisi aggregate</li>
              <li>Prevenire frodi e garantire la sicurezza della piattaforma</li>
              <li>Adempiere obblighi legali e fiscali</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">4. Base Giuridica del Trattamento</h2>
            <p className="mb-4">Il trattamento dei dati si basa su:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Contratto:</strong> per fornire i servizi richiesti</li>
              <li><strong>Consenso:</strong> per comunicazioni marketing (revocabile in qualsiasi momento)</li>
              <li><strong>Legittimo interesse:</strong> per migliorare il servizio e prevenire frodi</li>
              <li><strong>Obbligo legale:</strong> per adempiere a obblighi fiscali e normativi</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">5. Condivisione dei Dati</h2>
            <p className="mb-4">I tuoi dati possono essere condivisi con:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Altri utenti:</strong> informazioni necessarie per completare prenotazioni (nome, date, richieste)</li>
              <li><strong>Provider di pagamento:</strong> per elaborare transazioni in modo sicuro</li>
              <li><strong>Provider di servizi:</strong> hosting, analytics, email service (con adeguate garanzie)</li>
              <li><strong>Autorità:</strong> se richiesto per legge o per proteggere diritti</li>
            </ul>
            <p className="mb-4">
              <strong>Non vendiamo</strong> i tuoi dati personali a terze parti.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">6. Conservazione dei Dati</h2>
            <p className="mb-4">
              Conserviamo i tuoi dati personali solo per il tempo necessario alle finalità indicate:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account attivi:</strong> fino alla cancellazione dell'account</li>
              <li><strong>Dati transazionali:</strong> 10 anni (obbligo fiscale italiano)</li>
              <li><strong>Log di sistema:</strong> 12 mesi</li>
              <li><strong>Account cancellati:</strong> dati anonimizzati dopo 90 giorni</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">7. Diritti dell'Utente (GDPR)</h2>
            <p className="mb-4">Hai diritto a:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Accesso:</strong> ottenere copia dei tuoi dati</li>
              <li><strong>Rettifica:</strong> correggere dati inesatti</li>
              <li><strong>Cancellazione:</strong> richiedere la cancellazione ("diritto all'oblio")</li>
              <li><strong>Limitazione:</strong> limitare il trattamento in determinate circostanze</li>
              <li><strong>Portabilità:</strong> ricevere i dati in formato strutturato</li>
              <li><strong>Opposizione:</strong> opporti al trattamento per determinate finalità</li>
              <li><strong>Revoca consenso:</strong> revocare il consenso senza pregiudicare la liceità del trattamento</li>
              <li><strong>Reclamo:</strong> presentare reclamo al Garante per la Protezione dei Dati Personali</li>
            </ul>
            <p className="mb-4">
              Per esercitare i tuoi diritti, contattaci a: <strong>privacy@faciledigitale.it</strong>
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">8. Cookies</h2>
            <p className="mb-4">
              Utilizziamo cookies essenziali per il funzionamento del Servizio e cookies analitici per 
              migliorare l'esperienza utente. Puoi gestire le preferenze sui cookies nelle impostazioni 
              del tuo browser.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">9. Sicurezza</h2>
            <p className="mb-4">
              Implementiamo misure tecniche e organizzative appropriate per proteggere i tuoi dati:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Crittografia SSL/TLS per le comunicazioni</li>
              <li>Password criptate con algoritmi sicuri (bcrypt)</li>
              <li>Accesso limitato ai dati solo a personale autorizzato</li>
              <li>Backup regolari e sistemi di disaster recovery</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">10. Trasferimenti Internazionali</h2>
            <p className="mb-4">
              I dati sono conservati su server ubicati nell'Unione Europea. Eventuali trasferimenti 
              extra-UE avvengono solo con adeguate garanzie (es. Standard Contractual Clauses).
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">11. Modifiche alla Privacy Policy</h2>
            <p className="mb-4">
              Potremmo aggiornare questa Privacy Policy. Ti informeremo di modifiche significative 
              tramite email o notifica sulla piattaforma.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">12. Minori</h2>
            <p className="mb-4">
              Il Servizio non è destinato a minori di 18 anni. Non raccogliamo consapevolmente 
              dati di minori.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-8">13. Contatti</h2>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="font-semibold mb-2">Titolare del Trattamento:</p>
              <p><strong>Facile Digitale Srls</strong></p>
              <p>Indirizzo: [Da completare]</p>
              <p>Email: privacy@faciledigitale.it</p>
              <p>PEC: [Da completare]</p>
              <p>P.IVA: [Da completare]</p>
              
              <p className="font-semibold mt-4 mb-2">Garante per la Protezione dei Dati Personali:</p>
              <p>Piazza Venezia, 11 - 00187 Roma</p>
              <p>Tel: (+39) 06.696771</p>
              <p>Web: www.garanteprivacy.it</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
